import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Save, X, Vote, Play, Square, Minus, Copy, Link2, Tv } from "lucide-react";
import { api } from "@/lib/api";

const EMPTY_POLL = {
  title: "", question: "", image: "", allow_multiple: false,
  options: [{ text: "", image: "" }, { text: "", image: "" }],
  home_display: "list",
  home_show_results: false,
  results_theme: "light",
  results_bg_type: "solid",
  results_bg_color: "#111111",
  results_bg_image: "",
  results_hide_totals: false,
};

export default function AdminPollsTab() {
  const [polls, setPolls] = useState([]);
  const [draft, setDraft] = useState(null);

  const reload = async () => {
    try {
      const r = await api.get("/admin/polls-all");
      setPolls(r.data || []);
    } catch { toast.error("Error cargando"); }
  };
  useEffect(() => { reload(); }, []);

  const save = async () => {
    if (!draft.title) { toast.error("Título requerido"); return; }
    const validOpts = (draft.options || []).filter((o) => o.text?.trim());
    if (validOpts.length < 2) { toast.error("Mínimo 2 opciones"); return; }
    try {
      const payload = { ...draft, options: validOpts };
      if (draft.id) {
        await api.put(`/admin/polls-all/${draft.id}`, payload);
      } else {
        await api.post("/admin/polls-all", payload);
      }
      toast.success("Encuesta guardada");
      setDraft(null);
      await reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar encuesta y sus votos?")) return;
    await api.delete(`/admin/polls-all/${id}`);
    await reload();
    toast.success("Eliminada");
  };

  const openClose = async (poll, action) => {
    await api.post(`/admin/polls-all/${poll.id}/${action}`);
    await reload();
    toast.success(action === "open" ? "Encuesta abierta" : "Encuesta cerrada");
  };

  const adjust = async (poll, optionId, delta) => {
    await api.post(`/admin/polls-all/${poll.id}/adjust`, { option_id: optionId, delta });
    await reload();
  };

  const copyLink = async (poll, kind = "vote") => {
    const base = window.location.origin;
    const url = kind === "tv"
      ? `${base}/encuesta/${poll.slug}/tv`
      : kind === "results"
      ? `${base}/encuesta/${poll.slug}/resultados`
      : `${base}/encuesta/${poll.slug}`;
    await navigator.clipboard.writeText(url);
    toast.success(kind === "tv" ? "OUTPUT TV LINK copiado" : "Enlace copiado");
  };

  const updateOption = (idx, patch) => {
    const opts = [...draft.options];
    opts[idx] = { ...opts[idx], ...patch };
    setDraft({ ...draft, options: opts });
  };
  const addOption = () => setDraft({ ...draft, options: [...draft.options, { text: "", image: "" }] });
  const removeOption = (idx) => {
    if (draft.options.length <= 2) return;
    setDraft({ ...draft, options: draft.options.filter((_, i) => i !== idx) });
  };

  return (
    <div data-testid="admin-polls-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-headline uppercase text-3xl md:text-4xl flex items-center gap-2"><Vote size={26} /> Encuestas</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>Creá encuestas, sumá/restá votos manualmente, publicalas en la home</p>
        </div>
        <button onClick={() => setDraft({ ...EMPTY_POLL })} className="btn-accent" data-testid="admin-polls-new">
          <Plus size={16} /> Nueva encuesta
        </button>
      </div>

      {polls.length === 0 ? (
        <p className="text-center py-12 text-sm" style={{ color: "var(--text-muted)" }}>Sin encuestas todavía.</p>
      ) : (
        <div className="space-y-4">
          {polls.map((p) => (
            <div key={p.id} className="border rounded-lg p-4" style={{ borderColor: "var(--border)" }} data-testid={`admin-poll-row-${p.id}`}>
              <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${p.status === "open" ? "bg-green-100 text-green-700" : p.status === "closed" ? "bg-gray-200 text-gray-600" : "bg-yellow-100 text-yellow-700"}`}>
                      {p.status}
                    </span>
                    <span className="text-xs" style={{ color: "var(--text-muted)" }}>/{p.slug}</span>
                  </div>
                  <h3 className="font-bold text-base">{p.title}</h3>
                  {p.question && <p className="text-sm" style={{ color: "var(--text-muted)" }}>{p.question}</p>}
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  {p.status !== "open" ? (
                    <button onClick={() => openClose(p, "open")} className="text-xs px-3 py-1.5 rounded bg-green-600 text-white font-bold uppercase flex items-center gap-1" data-testid={`admin-poll-open-${p.id}`}>
                      <Play size={12} /> Abrir
                    </button>
                  ) : (
                    <button onClick={() => openClose(p, "close")} className="text-xs px-3 py-1.5 rounded bg-gray-700 text-white font-bold uppercase flex items-center gap-1" data-testid={`admin-poll-close-${p.id}`}>
                      <Square size={12} /> Cerrar
                    </button>
                  )}
                  <button onClick={() => copyLink(p, "vote")} className="text-xs px-2 py-1.5 rounded border font-bold flex items-center gap-1" style={{ borderColor: "var(--border)" }} title="Link votación" data-testid={`admin-poll-copy-vote-${p.id}`}>
                    <Link2 size={12} />
                  </button>
                  <button onClick={() => copyLink(p, "results")} className="text-xs px-2 py-1.5 rounded border font-bold flex items-center gap-1" style={{ borderColor: "var(--border)" }} title="Link resultados" data-testid={`admin-poll-copy-results-${p.id}`}>
                    <Copy size={12} />
                  </button>
                  <button onClick={() => copyLink(p, "tv")} className="text-xs px-2 py-1.5 rounded font-bold flex items-center gap-1 text-white" style={{ background: "#ec4899" }} title="OUTPUT TV LINK (16:9 para TV)" data-testid={`admin-poll-copy-tv-${p.id}`}>
                    <Tv size={12} /> TV
                  </button>
                  <button onClick={() => setDraft(p)} className="text-xs px-2 py-1.5 rounded border font-bold" style={{ borderColor: "var(--border)" }} data-testid={`admin-poll-edit-${p.id}`}><Edit size={12} /></button>
                  <button onClick={() => del(p.id)} className="text-xs px-2 py-1.5 rounded border font-bold" style={{ borderColor: "var(--border)" }} data-testid={`admin-poll-delete-${p.id}`}><Trash2 size={12} /></button>
                </div>
              </div>
              <div className="space-y-1.5">
                {(p.options || []).map((o) => (
                  <div key={o.id} className="flex items-center gap-3 text-sm">
                    <span className="flex-1 truncate">{o.text}</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => adjust(p, o.id, -1)} className="w-6 h-6 rounded border flex items-center justify-center hover:bg-red-50" style={{ borderColor: "var(--border)" }} data-testid={`admin-poll-decr-${p.id}-${o.id}`}><Minus size={11} /></button>
                      <span className="w-12 text-center font-bold" data-testid={`admin-poll-votes-${p.id}-${o.id}`}>{o.votes || 0}</span>
                      <button onClick={() => adjust(p, o.id, 1)} className="w-6 h-6 rounded border flex items-center justify-center hover:bg-green-50" style={{ borderColor: "var(--border)" }} data-testid={`admin-poll-incr-${p.id}-${o.id}`}><Plus size={11} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor modal */}
      {draft && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setDraft(null)}>
          <div className="rounded-lg p-6 max-w-2xl w-full max-h-[92vh] overflow-y-auto" style={{ background: "var(--surface)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-headline uppercase text-xl">{draft.id ? "Editar" : "Nueva"} encuesta</h2>
              <button onClick={() => setDraft(null)}><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <input type="text" placeholder="Título" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="w-full px-3 py-2 border rounded text-sm bg-transparent" style={{ borderColor: "var(--border)" }} data-testid="admin-poll-title" />
              <textarea placeholder="Pregunta / contexto (opcional)" value={draft.question || ""} onChange={(e) => setDraft({ ...draft, question: e.target.value })} rows={2} className="w-full px-3 py-2 border rounded text-sm bg-transparent" style={{ borderColor: "var(--border)" }} />
              <input type="text" placeholder="URL imagen portada (opcional)" value={draft.image || ""} onChange={(e) => setDraft({ ...draft, image: e.target.value })} className="w-full px-3 py-2 border rounded text-sm bg-transparent" style={{ borderColor: "var(--border)" }} data-testid="admin-poll-image" />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!draft.allow_multiple} onChange={(e) => setDraft({ ...draft, allow_multiple: e.target.checked })} data-testid="admin-poll-multiple" />
                Permitir varias opciones por voto
              </label>

              <div className="border-t pt-3" style={{ borderColor: "var(--border)" }}>
                <p className="text-xs font-bold uppercase tracking-wider mb-2">Visualización en la home</p>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <label className={`p-3 border rounded cursor-pointer text-center ${draft.home_display === "list" ? "bg-[#ec4899] text-white" : ""}`} style={{ borderColor: "var(--border)" }}>
                    <input type="radio" name="home_display" value="list" checked={draft.home_display === "list"} onChange={() => setDraft({ ...draft, home_display: "list" })} className="hidden" data-testid="admin-poll-display-list" />
                    <p className="text-xs font-bold uppercase">📋 Lista</p>
                    <p className="text-[10px] mt-1 opacity-75">Con imagen portada + opciones apiladas</p>
                  </label>
                  <label className={`p-3 border rounded cursor-pointer text-center ${draft.home_display === "cards" ? "bg-[#ec4899] text-white" : ""}`} style={{ borderColor: "var(--border)" }}>
                    <input type="radio" name="home_display" value="cards" checked={draft.home_display === "cards"} onChange={() => setDraft({ ...draft, home_display: "cards" })} className="hidden" data-testid="admin-poll-display-cards" />
                    <p className="text-xs font-bold uppercase">🃏 Cards</p>
                    <p className="text-[10px] mt-1 opacity-75">Fotos grandes + botón VOTAR dorado</p>
                  </label>
                </div>
                <label className="flex items-center gap-2 text-sm p-3 border rounded" style={{ borderColor: "var(--border)" }}>
                  <input type="checkbox" checked={!!draft.home_show_results} onChange={(e) => setDraft({ ...draft, home_show_results: e.target.checked })} data-testid="admin-poll-show-results-home" />
                  <span>
                    <span className="block font-bold">Mostrar resultados en el home</span>
                    <span className="text-[10px] opacity-70">Si está apagado, los usuarios votan sin ver porcentajes hasta la página de resultados</span>
                  </span>
                </label>
              </div>

              <div className="border-t pt-3" style={{ borderColor: "var(--border)" }}>
                <div className="flex justify-between items-center mb-2">
                  <p className="text-xs font-bold uppercase tracking-wider">Opciones</p>
                  <button onClick={addOption} className="text-xs px-2 py-1 border rounded" style={{ borderColor: "var(--border)" }} data-testid="admin-poll-add-option">+ Agregar</button>
                </div>
                <div className="space-y-2">
                  {draft.options.map((o, i) => (
                    <div key={i} className="flex gap-2">
                      <input type="text" placeholder={`Opción ${i + 1}`} value={o.text || ""} onChange={(e) => updateOption(i, { text: e.target.value })} className="flex-1 px-3 py-2 border rounded text-sm bg-transparent" style={{ borderColor: "var(--border)" }} data-testid={`admin-poll-option-text-${i}`} />
                      <input type="text" placeholder="URL imagen" value={o.image || ""} onChange={(e) => updateOption(i, { image: e.target.value })} className="w-40 px-2 py-2 border rounded text-xs bg-transparent" style={{ borderColor: "var(--border)" }} />
                      {draft.options.length > 2 && (
                        <button onClick={() => removeOption(i)} className="px-2 border rounded" style={{ borderColor: "var(--border)" }}><Trash2 size={12} /></button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t pt-3" style={{ borderColor: "var(--border)" }}>
                <p className="text-xs font-bold uppercase tracking-wider mb-2">Página de resultados — apariencia</p>
                <div className="grid grid-cols-2 gap-2">
                  <select value={draft.results_theme} onChange={(e) => setDraft({ ...draft, results_theme: e.target.value })} className="px-3 py-2 border rounded text-sm bg-transparent" style={{ borderColor: "var(--border)" }}>
                    <option value="light">Tema claro</option>
                    <option value="dark">Tema oscuro</option>
                  </select>
                  <select value={draft.results_bg_type} onChange={(e) => setDraft({ ...draft, results_bg_type: e.target.value })} className="px-3 py-2 border rounded text-sm bg-transparent" style={{ borderColor: "var(--border)" }}>
                    <option value="solid">Fondo sólido</option>
                    <option value="image">Fondo con imagen</option>
                    <option value="transparent">Transparente</option>
                  </select>
                </div>
                {draft.results_bg_type === "solid" && (
                  <input type="color" value={draft.results_bg_color || "#111111"} onChange={(e) => setDraft({ ...draft, results_bg_color: e.target.value })} className="w-full h-10 border rounded mt-2" style={{ borderColor: "var(--border)" }} />
                )}
                {draft.results_bg_type === "image" && (
                  <input type="text" placeholder="URL imagen de fondo" value={draft.results_bg_image || ""} onChange={(e) => setDraft({ ...draft, results_bg_image: e.target.value })} className="w-full px-3 py-2 border rounded text-sm bg-transparent mt-2" style={{ borderColor: "var(--border)" }} />
                )}
                <label className="flex items-center gap-2 text-sm p-3 border rounded mt-2" style={{ borderColor: "var(--border)" }}>
                  <input type="checkbox" checked={!!draft.results_hide_totals} onChange={(e) => setDraft({ ...draft, results_hide_totals: e.target.checked })} data-testid="admin-poll-hide-totals" />
                  <span>
                    <span className="block font-bold">Ocultar votos, dejar solo porcentajes</span>
                    <span className="text-[10px] opacity-70">Oculta el total general y los "N votos" por opción — muestra sólo % en las páginas de resultados y OUTPUT TV</span>
                  </span>
                </label>
              </div>

              <button onClick={save} className="btn-accent w-full" data-testid="admin-poll-save"><Save size={14} /> Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
